import { deriveBrandPalette, type BrandColors, type Locale } from '@nexus/core';
import { useMediaQuery } from '@nexus/ui';
import { useEffect } from 'react';

export type ThemeSetting = 'system' | 'light' | 'dark';

export interface Appearance {
  theme: ThemeSetting;
  density: 'comfortable' | 'compact';
  motion: 'system' | 'reduced';
  locale: Locale;
}

const STORAGE_KEY = 'nexus.appearance';

/**
 * The last appearance on this device, applied before the session is loaded so the page does not
 * flash in the wrong theme. Only a convenience: the setting itself lives on the server.
 */
export function storedAppearance(): Partial<Appearance> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Appearance>) : {};
  } catch {
    return {};
  }
}

function store(appearance: Appearance): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    // Private mode or blocked storage: the server value applies on the next load.
  }
}

/** Sets theme, density, motion and language on the document root. */
export function useApplyAppearance(appearance: Appearance): void {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const dark = appearance.theme === 'dark' || (appearance.theme === 'system' && prefersDark);
  const { density, motion, locale } = appearance;

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = dark ? 'dark' : 'light';
    root.dataset.density = density;
    if (motion === 'reduced') root.dataset.motion = 'reduced';
    else delete root.dataset.motion;
    root.lang = locale;
    store({ theme: appearance.theme, density, motion, locale });
  }, [dark, density, motion, locale, appearance.theme]);
}

export function useResolvedTheme(theme: ThemeSetting): 'light' | 'dark' {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  return theme === 'dark' || (theme === 'system' && prefersDark) ? 'dark' : 'light';
}

const BRAND_PROPERTIES = ['--brand', '--brand-text', '--brand-solid', '--brand-solid-fg'] as const;

/** Brand tokens of a company for one theme, derived with checked contrast (design-system.md). */
export function brandProperties(
  primary: string,
  theme: 'light' | 'dark',
): Record<(typeof BRAND_PROPERTIES)[number], string> {
  const colors: BrandColors = deriveBrandPalette(primary)[theme];
  return {
    '--brand': colors.primary,
    '--brand-text': colors.primaryText,
    '--brand-solid': colors.primarySolid,
    '--brand-solid-fg': colors.primarySolidForeground,
  };
}

/**
 * Applies the colour of the active company as inline custom properties on the root, which a
 * strict Content Security Policy allows. Without a company the platform tokens apply (login).
 */
export function useBrandColor(primary: string | null | undefined, theme: 'light' | 'dark'): void {
  useEffect(() => {
    const root = document.documentElement;
    if (!primary) {
      for (const property of BRAND_PROPERTIES) root.style.removeProperty(property);
      return;
    }
    for (const [property, value] of Object.entries(brandProperties(primary, theme))) {
      root.style.setProperty(property, value);
    }
  }, [primary, theme]);
}
